import { Component, computed, inject, linkedSignal, signal } from "@angular/core"
import { NgClass } from "@angular/common"
import { httpResource } from "@angular/common/http"
import { ActivatedRoute, Router } from "@angular/router"
import { toSignal } from "@angular/core/rxjs-interop"
import { firstValueFrom, map } from "rxjs"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { FormField, form, submit } from "@angular/forms/signals"
import { ModifyNews, ModifyNewsFactory, ModifyObjectFactory, NEWS_FIELD_LABELS, News } from "../../models"
import { NewsService, ToastService } from "../../services"
import { JsonPatchDocumentGenerator } from "../../helpers/JsonPatchDocumentGenerator"
import { BooleanInputComponent } from "../../components/boolean-input/boolean-input.component"
import { TextInputComponent } from "../../components/text-input/text-input.component"
import { FileInputComponent } from "../../components/file-input/file-input.component"
import {
    ConfirmDialogComponent,
    DictionaryInputComponent,
} from "../../components"
import {
    PATCH_IGNORED_FIELDS,
    describeError,
    invalidFieldsDetail,
    toServerFieldErrors,
} from "../../helpers/FormErrors"

@Component({
    selector: "valben-news-edit",
    templateUrl: "./news-edit.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        NgClass,
        MatProgressSpinnerModule,
        FormField,
        DictionaryInputComponent,
        BooleanInputComponent,
        TextInputComponent,
        FileInputComponent,
    ],
})
export class NewsEditPage {
    private _newsService = inject(NewsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)
    private _dialog = inject(MatDialog)
    private _patchGenerator = new JsonPatchDocumentGenerator()

    private _masterId = toSignal(inject(ActivatedRoute).paramMap.pipe(map(p => p.get("masterId") ?? "")), {
        initialValue: "",
    })

    news = httpResource<News>(() => {
        const masterId = this._masterId()
        return masterId ? this._newsService.ByMasterIdUrl(masterId) : undefined
    })

    saving = signal(false)

    model = linkedSignal<ModifyNews>(() => {
        const news = this.news.value()
        return news ? ModifyNewsFactory.from(news) : ModifyNewsFactory.create()
    })

    newsForm = form(this.model)

    loading = computed(() => this.news.isLoading() || this.saving())
    loaded = computed(() => this.news.hasValue())

    async save(): Promise<void> {
        const original = this.news.value()
        if (!original) return

        this.saving.set(true)
        let failed = false

        const saved = await submit(this.newsForm, {
            action: async f => {
                const patch = this._patchGenerator.getJsonPatchDocument(
                    ModifyNewsFactory.from(original),
                    f().value(),
                    PATCH_IGNORED_FIELDS
                )

                try {
                    await firstValueFrom(
                        this._newsService.Modify(
                            this._masterId(),
                            ModifyObjectFactory.create(patch, original.version)
                        )
                    )
                    return null
                } catch (err) {
                    failed = true
                    const { detail, errors } = toServerFieldErrors(err, this.newsForm, NEWS_FIELD_LABELS)
                    this._error(detail)
                    return errors
                }
            },
            onInvalid: () => this._error(invalidFieldsDetail(this.newsForm, NEWS_FIELD_LABELS)),
        })

        this.saving.set(false)
        if (failed || !saved) return

        this._messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Uspješno izmijenjeno",
            life: 10000,
        })
        this.back()
    }

    async delete(): Promise<void> {
        this.saving.set(true)
        try {
            await firstValueFrom(this._newsService.Delete(this._masterId()))
            this._messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Uspješno obrisano",
                life: 10000,
            })
            this.back()
        } catch (err) {
            this._error(describeError(err).message)
        } finally {
            this.saving.set(false)
        }
    }

    async showDeleteModal(): Promise<void> {
        const confirmed = await firstValueFrom(
            this._dialog
                .open(ConfirmDialogComponent, {
                    width: "35rem",
                    data: { header: "Jeste li sigurni da želite obrisati entitet?" },
                })
                .afterClosed()
        )
        if (confirmed) await this.delete()
    }

    back(): void {
        this._router.navigate(["/news"])
    }

    private _error(detail: string): void {
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }
}
